import React, { useRef } from 'react';
import { renderFieldWithLinks } from 'utils/linkifyText';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { AiEvaluation, MatchRow } from './types';
import { riskTone, titleCase } from './utils';
import ScreeningDialog from './ScreeningDialog';
import { splitRiskSignals } from 'utils/entityRiskSignals';

const hasDisplayValue = (value: any) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.filter(Boolean).length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim() !== '';
};

const formatFieldValue = (value: any): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value ?? '');
};

interface MatchProps {
  rows: MatchRow[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}

export const MatchDrawer: React.FC<MatchProps> = ({
  rows,
  index,
  onIndex,
  onClose,
}) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const row = rows[index];
  if (!row) {
    return null;
  }
  const source = row.source || {};
  const { signals: riskSignals, rest: details } = splitRiskSignals(source);
  const noPrev = index <= 0;
  const noNext = index >= rows.length - 1;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      onIndex(Math.min(rows.length - 1, index + 1));
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      onIndex(Math.max(0, index - 1));
    }
  };

  return (
    <ScreeningDialog
      label='Product details'
      panelClassName='drawer-panel entity-record-drawer product-record-drawer psc-record-drawer'
      onClose={onClose}
      onKeyDown={onKeyDown}
      initialFocus={closeRef}
    >
      <div className='drawer-header entity-record-drawer-header'>
        <div className='entity-record-drawer-nav'>
          <button
            type='button'
            onClick={() => onIndex(index - 1)}
            disabled={noPrev}
            aria-label='Open previous product'
            title='Open previous product'
          >
            <ChevronLeftIcon sx={{ fontSize: 22 }} />
          </button>
          <button
            type='button'
            onClick={() => onIndex(index + 1)}
            disabled={noNext}
            aria-label='Open next product'
            title='Open next product'
          >
            <ChevronRightIcon sx={{ fontSize: 22 }} />
          </button>
        </div>
        <h2 className='drawer-title entity-record-drawer-title'>
          <span className='entity-record-drawer-title-main'>
            {source['Product Name'] || 'Product Details'}
          </span>
        </h2>
        <button
          ref={closeRef}
          type='button'
          className='drawer-close-btn entity-record-drawer-close'
          onClick={onClose}
          aria-label='Close product details'
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </button>
      </div>
      <div className='drawer-body entity-record-drawer-body'>
        {riskSignals.length > 0 && (
          <div>
            <div className='drawer-section-title'>Risk Signals</div>
            <div className='drawer-kv-box entity-record-drawer-kv-box'>
              {riskSignals.map(({ label, value }) => (
                <div
                  className='drawer-kv-row entity-record-drawer-row'
                  key={label}
                >
                  <span className='drawer-kv-label'>{label}</span>
                  <span className='drawer-kv-value'>
                    {renderFieldWithLinks(formatFieldValue(value))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className='drawer-section-title'>Product Details</div>
          <div className='drawer-kv-box entity-record-drawer-kv-box'>
            {Object.entries(details)
              .filter(([, value]) => hasDisplayValue(value))
              .map(([label, value]) => (
                <div
                  className='drawer-kv-row entity-record-drawer-row'
                  key={label}
                >
                  <span className='drawer-kv-label'>{label}</span>
                  <span className='drawer-kv-value'>
                    {renderFieldWithLinks(formatFieldValue(value))}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </ScreeningDialog>
  );
};

interface EvalProps {
  data: AiEvaluation;
  sub: string;
  listsRead: number;
  totalMatches: number;
  showsTradeCategory: boolean;
  onClose: () => void;
}

export const EvaluationDrawer: React.FC<EvalProps> = ({
  data,
  sub,
  listsRead,
  totalMatches,
  showsTradeCategory,
  onClose,
}) => {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const metrics = [
    { k: 'HTS code', v: data.hs_code || 'N/A', tone: '' },
    ...(showsTradeCategory
      ? [
          {
            k: 'Trade category',
            v: data.trade_category ? titleCase(data.trade_category) : '—',
            tone: '',
          },
        ]
      : []),
    {
      k: 'Risk',
      v: data.risk_level ? titleCase(data.risk_level) : '—',
      tone: riskTone(data.risk_level),
    },
    {
      k: 'Burden',
      v: data.burden ? titleCase(data.burden) : '—',
      tone: riskTone(data.burden),
    },
  ];
  return (
    <ScreeningDialog
      label='AI evaluation'
      panelClassName='psc-drawer'
      onClose={onClose}
      initialFocus={closeRef}
    >
      <div className='drawer-header'>
        <div className='psc-eval-heading'>
          <h2 className='drawer-title'>AI evaluation</h2>
          <p className='psc-eval-sub'>{sub}</p>
        </div>
        <button
          ref={closeRef}
          type='button'
          className='drawer-close-btn'
          onClick={onClose}
          aria-label='Close AI evaluation'
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </button>
      </div>
      <div className='drawer-body'>
        <div className='psc-eval-metrics'>
          {metrics.map((m) => (
            <div key={m.k} className='drawer-impact-card'>
              <span className='drawer-impact-label'>{m.k}</span>
              <span
                className={`drawer-impact-value${
                  m.tone && m.tone !== 'none' ? ` psc-tone--${m.tone}` : ''
                }`}
              >
                {m.v}
              </span>
            </div>
          ))}
        </div>
        <p className='drawer-text-block psc-eval-prose'>
          {data.explanation || 'The evaluation returned no explanation.'}
        </p>
        {data.key_factors.length > 0 && (
          <div>
            <div className='drawer-section-title'>Key factors</div>
            <div className='drawer-kv-box'>
              {data.key_factors.map((k, i) => (
                <div key={i} className='drawer-kv-row psc-factor-row'>
                  <span className='drawer-kv-label'>
                    {k['List Name'] || k.list_name || '—'}
                  </span>
                  <span className='drawer-kv-value'>
                    {k['HS Code'] ? `HS ${k['HS Code']}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className='psc-eval-note'>
          Generated {data.screenDate || 'today'}. The{' '}
          {totalMatches.toLocaleString()}{' '}
          {totalMatches === 1 ? 'match is' : 'matches are'} reduced to one per
          list before evaluation, so the model read {listsRead}{' '}
          {listsRead === 1 ? 'record' : 'records'}.
        </p>
      </div>
    </ScreeningDialog>
  );
};
